/**
 * =============================================================================
 * Componente: ReferralStatusBadge
 * Versão: 1.0.0
 * Release: 2026-05-02 — Util compartilhado para status de INDICAÇÃO
 *
 * Descrição:
 *   Padroniza a exibição do status de uma indicação (pending / qualified /
 *   converted / lost / cancelled) em qualquer tela do sistema.
 *
 *   Visual oficial: "dot colorido + texto" (mesmo padrão usado no painel do
 *   afiliado em `AffiliateReferrals`). Sem badge cheio de cor — apenas uma
 *   bolinha pequena seguida do label, mantendo a tabela limpa e legível.
 *
 *   Uso:
 *     <ReferralStatusBadge status={r.status} />
 *
 *   Também exporta `REFERRAL_STATUS_CONFIG` para casos onde se precisa
 *   apenas das classes de cor (ex.: barra lateral colorida da linha).
 *
 *   IMPORTANTE: Sempre usar este util em vez de redeclarar mapas locais —
 *   garante consistência visual entre listagens admin, painel do afiliado,
 *   detalhes e quaisquer outros pontos que exibam status de indicação.
 * =============================================================================
 */
import { cn } from '@/lib/utils';

/** Status canônicos de uma indicação. */
export type ReferralStatus =
  | 'pending'
  | 'qualified'
  | 'converted'
  | 'lost'
  | 'cancelled';

/**
 * Configuração visual única por status.
 *  - `label` : texto exibido em PT-BR
 *  - `dot`   : classe Tailwind para a bolinha colorida (badge)
 *  - `bar`   : classe Tailwind para barras laterais (mesma família de cor)
 */
export const REFERRAL_STATUS_CONFIG: Record<
  ReferralStatus,
  { label: string; dot: string; bar: string }
> = {
  // Âmbar: aguardando ação
  pending:   { label: 'Pendente',    dot: 'bg-amber-500', bar: 'bg-amber-500' },
  // Azul: lead já qualificado, mas ainda não convertido
  qualified: { label: 'Qualificado', dot: 'bg-blue-500',  bar: 'bg-blue-500'  },
  // Verde: indicação convertida em venda
  converted: { label: 'Convertido',  dot: 'bg-green-500', bar: 'bg-green-500' },
  // Vermelho: descartada (perdida)
  lost:      { label: 'Perdido',     dot: 'bg-red-500',   bar: 'bg-red-500'   },
  // Vermelho: cancelada manualmente
  cancelled: { label: 'Cancelada',   dot: 'bg-red-500',   bar: 'bg-red-500'   },
};

interface ReferralStatusBadgeProps {
  /** Status da indicação (aceita string para compatibilidade com dados do backend). */
  status: string | null | undefined;
  /** Classes extras opcionais aplicadas ao wrapper. */
  className?: string;
  /**
   * Texto exibido quando status for nulo/desconhecido.
   * Padrão: travessão "—" estilizado como muted.
   */
  fallback?: React.ReactNode;
}

/**
 * Badge "dot + texto" para status de indicação.
 *
 * Renderiza uma bolinha pequena (8px) colorida seguida do label.
 * Texto usa `text-foreground` (cor padrão do tema) — o destaque vem da cor
 * do dot, mantendo a coluna leve visualmente em listagens densas.
 */
export function ReferralStatusBadge({
  status,
  className,
  fallback,
}: ReferralStatusBadgeProps) {
  // Sem status válido → fallback configurável.
  const cfg = status
    ? REFERRAL_STATUS_CONFIG[status as ReferralStatus]
    : null;

  if (!cfg) {
    return (
      <>{fallback ?? <span className="text-xs text-muted-foreground">—</span>}</>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium text-foreground whitespace-nowrap',
        className,
      )}
    >
      {/* Bolinha colorida — único elemento que carrega a cor do status */}
      <span
        aria-hidden
        className={cn('h-2 w-2 rounded-full shrink-0', cfg.dot)}
      />
      {cfg.label}
    </span>
  );
}
